import { createSlice, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit';
import axios from 'axios';
import { getApiErrorMessage } from '../../utils/apiError';

const API_BASE_URL = import.meta.env.VITE_API_URL as string;

const getAuthHeaders = (getState: () => unknown) => {
  const state = getState() as { auth?: { token?: string | null } };
  const token = state.auth?.token;
  return token ? { Authorization: `Bearer ${token}` } : {};
};

export interface VacationTypeItem {
  id: number;
  name: string;
  isIllness: boolean;
}

export interface Vacation {
  id: number;
  teacherId: number;
  vacationTypeId: number;
  vacationType?: VacationTypeItem;
  dateFrom: string;
  dateTo: string;
  createdAt?: string;
  updatedAt?: string;
}

export interface VacationStats {
  vacationDaysUsed: number;
  vacationDaysTotal: number;
  illnessDaysUsed: number;
  illnessDaysTotal: number;
  year: number;
}

interface VacationsState {
  vacations: Vacation[];
  types: VacationTypeItem[];
  stats: VacationStats | null;
  loading: boolean;
  error: string | null;
}

const initialState: VacationsState = {
  vacations: [],
  types: [],
  stats: null,
  loading: false,
  error: null,
};

export const fetchVacationTypesAsync = createAsyncThunk(
  'vacations/fetchTypes',
  async (_, { rejectWithValue, getState }) => {
    try {
      const response = await axios.get(`${API_BASE_URL}/vacations/types`, {
        headers: getAuthHeaders(getState),
      });
      return response.data.types as VacationTypeItem[];
    } catch (error) {
      return rejectWithValue(getApiErrorMessage(error, 'Failed to fetch vacation types'));
    }
  }
);

export const fetchVacationStatsAsync = createAsyncThunk(
  'vacations/fetchStats',
  async (_, { rejectWithValue, getState }) => {
    try {
      const response = await axios.get(`${API_BASE_URL}/vacations/stats`, {
        headers: getAuthHeaders(getState),
      });
      return response.data as VacationStats;
    } catch (error) {
      return rejectWithValue(getApiErrorMessage(error, 'Failed to fetch vacation stats'));
    }
  }
);

export const fetchVacationsAsync = createAsyncThunk(
  'vacations/fetchVacations',
  async (_, { rejectWithValue, getState }) => {
    try {
      const response = await axios.get(`${API_BASE_URL}/vacations`, {
        headers: getAuthHeaders(getState),
      });
      return response.data.vacations as Vacation[];
    } catch (error) {
      return rejectWithValue(getApiErrorMessage(error, 'Failed to fetch vacations'));
    }
  }
);

export const createVacationAsync = createAsyncThunk(
  'vacations/createVacation',
  async (data: { vacationTypeId: number; dateFrom: string; dateTo: string }, { rejectWithValue, getState }) => {
    try {
      const response = await axios.post(`${API_BASE_URL}/vacations`, data, {
        headers: getAuthHeaders(getState),
      });
      return response.data.vacation as Vacation;
    } catch (error) {
      return rejectWithValue(getApiErrorMessage(error, 'Failed to create vacation'));
    }
  }
);

export const updateVacationAsync = createAsyncThunk(
  'vacations/updateVacation',
  async ({ id, data }: { id: number; data: { vacationTypeId?: number; dateFrom?: string; dateTo?: string } }, { rejectWithValue, getState }) => {
    try {
      const response = await axios.put(`${API_BASE_URL}/vacations/${id}`, data, {
        headers: getAuthHeaders(getState),
      });
      return response.data.vacation as Vacation;
    } catch (error) {
      return rejectWithValue(getApiErrorMessage(error, 'Failed to update vacation'));
    }
  }
);

export const deleteVacationAsync = createAsyncThunk(
  'vacations/deleteVacation',
  async (id: number, { rejectWithValue, getState }) => {
    try {
      await axios.delete(`${API_BASE_URL}/vacations/${id}`, {
        headers: getAuthHeaders(getState),
      });
      return id;
    } catch (error) {
      return rejectWithValue(getApiErrorMessage(error, 'Failed to delete vacation'));
    }
  }
);

const vacationsSlice = createSlice({
  name: 'vacations',
  initialState,
  reducers: {
    clearError: (state) => {
      state.error = null;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchVacationTypesAsync.fulfilled, (state, action: PayloadAction<VacationTypeItem[]>) => {
        state.types = action.payload;
      })
      .addCase(fetchVacationStatsAsync.fulfilled, (state, action: PayloadAction<VacationStats>) => {
        state.stats = action.payload;
      })
      .addCase(fetchVacationsAsync.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchVacationsAsync.fulfilled, (state, action: PayloadAction<Vacation[]>) => {
        state.loading = false;
        state.vacations = action.payload;
      })
      .addCase(fetchVacationsAsync.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      })
      .addCase(createVacationAsync.fulfilled, (state, action: PayloadAction<Vacation>) => {
        state.vacations.unshift(action.payload);
      })
      .addCase(updateVacationAsync.fulfilled, (state, action: PayloadAction<Vacation>) => {
        const index = state.vacations.findIndex(v => v.id === action.payload.id);
        if (index !== -1) {
          state.vacations[index] = action.payload;
        }
      })
      .addCase(deleteVacationAsync.fulfilled, (state, action: PayloadAction<number>) => {
        state.vacations = state.vacations.filter(v => v.id !== action.payload);
      });
  },
});

export const { clearError } = vacationsSlice.actions;
export default vacationsSlice.reducer;
